/* eslint-disable react-hooks/exhaustive-deps */

import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import gql from "graphql-tag";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import UserContext from "../../context/User";
import EmbedContext from "../../context/Embed";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { abortOrder, orderStripe } from "../../apollo/server";
import { Header } from "../../components/Header";
import { PaymentProcessing } from "../../components/Checkout";
import ConfigurableValues from "../../config/constants";
import Analytics from "../../utils/analytics";
import useStyles from "./styles";

const ORDER_STRIPE = gql`
  ${orderStripe}
`;

const ABORT_ORDER = gql`
  ${abortOrder}
`;

function CheckoutForm({ order, orderId, serverUrl, totalAmount, clearCart, fetchOrders, paymentSucceeded }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const classes = useStyles();
  // Embed mode: stay on the order's page instead of the full orders list
  const { isEmbed } = useContext(EmbedContext);
  // The same blocking overlay the checkout screen uses, so pressing Pay
  // CONTINUES that flow rather than stalling behind a spinner in the button.
  // Everything after this press is invisible otherwise: confirmPayment is a
  // round trip to the bank, /stripe/confirm-payment then marks the order paid
  // and notifies the restaurant, and the orders refetch has to finish before
  // this screen is allowed to change. null | 'processing' | 'success'
  const [phase, setPhase] = useState(null);
  const processing = phase !== null;
  const [errorMsg, setErrorMsg] = useState(null);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      setPhase("processing");
      setErrorMsg(null);

      const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: returnUrl },
      });

      if (error) {
        setErrorMsg(error.message);
        setPhase(null);
        return;
      }

      if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        try {
          const resp = await fetch(`${serverUrl}stripe/confirm-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              paymentIntentId: paymentIntent.id,
              orderId,
            }),
          });
          const result = await resp.json();
          if (result.success) {
            paymentSucceeded.current = true;
            // The money has moved — say so before the refetch, not after it.
            setPhase("success");
            const confirmedAt = Date.now();
            if (result.loyaltyEarnedMessage) {
              sessionStorage.setItem("loyaltyEarnedMessage", result.loyaltyEarnedMessage);
            }
            clearCart();
            await fetchOrders();
            // The refetch usually outlasts this, so it rarely waits; it is here
            // so a fast one cannot flash the confirmation past too quickly to
            // read. Mirrors the checkout screen's own dwell.
            const dwell = 1200 - (Date.now() - confirmedAt);
            if (dwell > 0) await new Promise((r) => setTimeout(r, dwell));
            navigate(isEmbed ? `/order-detail/${orderId}` : "/orders", { replace: true });
            // Deliberately no setPhase(null): the overlay stays up until the
            // order screen takes over, so the last thing seen here is that it
            // worked — never a re-armed Pay button on an order already paid.
            return;
          }
          setErrorMsg(result.error || "Failed to confirm payment on server.");
        } catch (err) {
          setErrorMsg("Network error confirming payment.");
        }
      } else {
        setErrorMsg("Payment was not completed. Please try again.");
      }

      setPhase(null);
    },
    [stripe, elements, orderId, serverUrl, navigate, clearCart, fetchOrders, isEmbed]
  );

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMsg && (
        <Typography color="error" variant="body2" className={classes.errorText}>
          {errorMsg}
        </Typography>
      )}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || processing}
        className={classes.payButton}
      >
        {processing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Pay $${parseFloat(totalAmount).toFixed(2)}`
        )}
      </Button>
      <PaymentProcessing
        phase={phase}
        amount={totalAmount}
        paymentMethod="STRIPE_PAY"
        orderId={order?.orderId}
      />
    </form>
  );
}

function Stripe() {
  const { SERVER_URL, STRIPE_PUBLIC_KEY } = ConfigurableValues();
  const { clearCart, fetchOrders } = useContext(UserContext);
  // Embed mode: stay on the order's page instead of the full orders list
  const { isEmbed } = useContext(EmbedContext);
  const navigate = useNavigate();
  const classes = useStyles();
  const location = useLocation();
  const id = useMemo(() => new URLSearchParams(location.search).get("id") ?? null, [location.search]);
  // Checkout charges Stripe cards on its own page now; this screen is mostly
  // the landing spot for methods that authorise on another page. Stripe adds
  // its return params to the return_url (/stripe?id=…). A return to an old
  // #/stripe?id= URL has its params merged into the query by index.js, but
  // read the router's and the window's query both, as before.
  const returnParam = (name) =>
    new URLSearchParams(location.search).get(name) ??
    new URLSearchParams(window.location.search).get(name) ??
    null;
  const redirectPaymentIntent = useMemo(() => returnParam("payment_intent"), [location.search]);
  const redirectStatus = useMemo(() => returnParam("redirect_status"), [location.search]);

  const [clientSecret, setClientSecret] = useState(null);
  const [intentError, setIntentError] = useState(null);
  // Drives the overlay on the paths the form does not own: coming back from a
  // 3DS/Link redirect, where the charge is already authorised and all that is
  // left is the server confirm. null | 'processing' | 'success'
  const [returnPhase, setReturnPhase] = useState(null);

  const paymentSucceeded = useRef(false);
  const [abortOrderMutation] = useMutation(ABORT_ORDER);

  // Delete the order if user leaves without paying
  useEffect(() => {
    return () => {
      if (!paymentSucceeded.current && id) {
        abortOrderMutation({ variables: { id } }).catch(() => {});
      }
    };
  }, [id]);

  const stripePromise = useMemo(
    () => (STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null),
    [STRIPE_PUBLIC_KEY]
  );

  const { data, loading, error } = useQuery(ORDER_STRIPE, {
    variables: { id },
    skip: !id,
  });

  const order = data?.orderStripe;
  // Server stores orderAmount post-promo and deliveryCharges post-Black-Card,
  // so summing these gives the exact charge total. The blackCardDiscount
  // value is shown as an informational green row, not subtracted again.
  const totalAmount = order
    ? parseFloat(order.orderAmount) +
      parseFloat(order.deliveryCharges || 0) +
      parseFloat(order.tipping || 0) +
      parseFloat(order.taxationAmount || 0) +
      parseFloat(order.serviceFee || 0)
    : 0;

  useEffect(() => {
    Analytics.track(Analytics.events.NAVIGATE_TO_STRIPE);
  }, []);

  // Handle Stripe redirect return (Link, 3DS, etc.)
  useEffect(() => {
    if (!redirectPaymentIntent || !redirectStatus || !id || !SERVER_URL) return;

    if (redirectStatus === "succeeded") {
      setReturnPhase("processing");
      fetch(`${SERVER_URL}stripe/confirm-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentIntentId: redirectPaymentIntent, orderId: id }),
      })
        .then((r) => r.json())
        .then(async (result) => {
          if (result.success) {
            paymentSucceeded.current = true;
            setReturnPhase("success");
            const confirmedAt = Date.now();
            if (result.loyaltyEarnedMessage) {
              sessionStorage.setItem("loyaltyEarnedMessage", result.loyaltyEarnedMessage);
            }
            clearCart();
            await fetchOrders();
            const dwell = 1200 - (Date.now() - confirmedAt);
            if (dwell > 0) await new Promise((r) => setTimeout(r, dwell));
            navigate(isEmbed ? `/order-detail/${id}` : "/orders", { replace: true });
          } else {
            setReturnPhase(null);
            setIntentError(result.error || "Payment confirmation failed.");
          }
        })
        .catch(() => {
          setReturnPhase(null);
          setIntentError("Network error confirming payment.");
        });
    } else {
      setIntentError("Payment was not completed. Please try again.");
    }
  }, [redirectPaymentIntent, redirectStatus, id, SERVER_URL, clearCart, fetchOrders, navigate, isEmbed]);

  // Create PaymentIntent once we have the order (skip if this is a redirect return)
  useEffect(() => {
    if (!order || clientSecret || redirectPaymentIntent) return;

    fetch(`${SERVER_URL}stripe/create-payment-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderId: id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setIntentError(data.error || "Failed to create payment intent.");
        }
      })
      .catch((err) => setIntentError(err.message));
  }, [order, id, SERVER_URL, clientSecret]);

  if (error || intentError) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" justifyContent="center" alignItems="center" flex={1} style={{ minHeight: "55vh" }}>
            <Typography variant="h5" color="error">
              {error?.message || intentError}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (!id) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" justifyContent="center" alignItems="center" flex={1} style={{ minHeight: "55vh" }}>
            <Typography variant="h5" color="error">
              Missing order ID
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (loading || (!clientSecret && !redirectPaymentIntent) || !order) {
    return (
      <Grid container className={classes.root}>
        <Header />
        <Grid container item className={classes.mainContainer} justifyContent="center">
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ minHeight: "55vh" }}>
            <CircularProgress size={48} />
            <Typography variant="h6" style={{ marginTop: 20 }}>
              {loading ? "Loading order..." : redirectPaymentIntent ? "Confirming payment..." : "Preparing payment..."}
            </Typography>
          </Box>
        </Grid>
        {/* Arriving here is a CONTINUATION of the checkout overlay, which was
            still saying "taking you to secure payment" when this route replaced
            it. Without this the customer drops from that overlay onto a bare
            spinner on a new page — the seam that made paying feel like a second
            errand rather than the next step of one. Same overlay, same stages,
            so the handoff is invisible. Coming back from a 3DS/Link redirect is
            the other half of a payment already authorised, so that one shows the
            pay stages instead. */}
        <PaymentProcessing
          phase={redirectPaymentIntent ? returnPhase || "processing" : "processing"}
          amount={order ? totalAmount : null}
          paymentMethod={redirectPaymentIntent ? "STRIPE_PAY" : "STRIPE"}
          orderId={order?.orderId}
        />
      </Grid>
    );
  }

  const elementsOptions = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#5A9E6F",
        borderRadius: "8px",
      },
    },
  };

  return (
    <Grid container className={classes.root}>
      <Header />
      <Grid container item className={classes.mainContainer} justifyContent="center">
        <Grid item xs={12} sm={10} md={8} lg={6}>
          <Paper className={classes.paymentCard} elevation={3}>
            <Typography variant="h5" className={classes.title}>
              Complete Your Payment
            </Typography>
            {/* Says why this screen follows Place Order, which is otherwise a
                fair thing to wonder: the order exists but nothing has been
                charged yet, and leaving without paying aborts it (see the
                unmount cleanup above). */}
            <Typography
              variant="body2"
              align="center"
              style={{ color: "#6b7280", marginTop: 6 }}
            >
              Step 2 of 2 — your order is held until this payment goes through
            </Typography>
            <Divider style={{ margin: "16px 0" }} />

            {/* Order Summary */}
            <Box className={classes.orderSummary}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Order #{order.orderId}</strong>
              </Typography>
              <Box className={classes.itemRow}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  ${parseFloat(order.orderAmount).toFixed(2)}
                </Typography>
              </Box>
              {parseFloat(order.deliveryCharges) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Delivery</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.deliveryCharges).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.taxationAmount) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Tax</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.taxationAmount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.tipping) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Tip</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.tipping).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.serviceFee) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2">Service fee</Typography>
                  <Typography variant="body2">
                    ${parseFloat(order.serviceFee).toFixed(2)}
                  </Typography>
                </Box>
              )}
              {parseFloat(order.blackCardDiscount) > 0 && (
                <Box className={classes.itemRow}>
                  <Typography variant="body2" style={{ color: "#16a34a" }}>
                    Black Card Delivery Discount
                  </Typography>
                  <Typography variant="body2" style={{ color: "#16a34a", fontWeight: 600 }}>
                    -${parseFloat(order.blackCardDiscount).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Divider style={{ margin: "8px 0" }} />
              <Box className={classes.itemRow}>
                <Typography variant="subtitle1">
                  <strong>Total</strong>
                </Typography>
                <Typography variant="subtitle1">
                  <strong>${totalAmount.toFixed(2)}</strong>
                </Typography>
              </Box>
            </Box>

            <Divider style={{ margin: "16px 0" }} />

            {/* Stripe Elements Form */}
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm
                order={order}
                orderId={id}
                serverUrl={SERVER_URL}
                totalAmount={totalAmount}
                clearCart={clearCart}
                fetchOrders={fetchOrders}
                paymentSucceeded={paymentSucceeded}
              />
            </Elements>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default React.memo(Stripe);
